import './styles.css';
import flatpickr from 'flatpickr';
import type { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import { Japanese } from 'flatpickr/dist/l10n/ja';
import { onDayCreateHoliday, buildTimeSlotsPanel } from '../../vendors/flatpickr/utils';
import { createSharedSheetController, type SharedSheetController } from '../sheet/controller';

const CALENDAR_SELECTOR = 'input.calendar-sheet';
const BOUND_KEY = 'true';

type CalendarState = {
    input: HTMLInputElement;
    trigger: HTMLButtonElement;
    sheet: HTMLElement;
    panel: HTMLElement;
    backdrop: HTMLButtonElement;
    body: HTMLElement;
    fpContainer: HTMLElement;
    closeBtn: HTMLButtonElement;
    controller: SharedSheetController;
    fp: FlatpickrInstance | null;
    times: string[];
    sideSlots: boolean;
    combinedInput: HTMLInputElement | null;
    slotsWrapper: HTMLElement | null;
};

let initialized = false;
const states = new WeakMap<HTMLInputElement, CalendarState>();

export function initCalendarComponent(): void {
    if (initialized) return;
    initialized = true;
    document.addEventListener('ui:update', () => mount(document));
}

function mount(scope: ParentNode): void {
    const inputs = Array.from(scope.querySelectorAll<HTMLInputElement>(CALENDAR_SELECTOR));
    inputs.forEach((input) => {
        if (input.dataset.calendarSheetBound === BOUND_KEY) return;
        input.dataset.calendarSheetBound = BOUND_KEY;
        const state = createState(input);
        states.set(input, state);
        bind(state);
        syncTriggerLabel(state);
    });
}

function createState(input: HTMLInputElement): CalendarState {
    const times: string[] = input.dataset.flatpickrTimes
        ? (JSON.parse(input.dataset.flatpickrTimes) as string[])
        : [];
    const sideSlots = input.hasAttribute('data-flatpickr-side-slots');

    // 時間スロットがある場合はフォーム送信値を保持する hidden input を作成
    let combinedInput: HTMLInputElement | null = null;
    if (times.length > 0 && input.name) {
        combinedInput = document.createElement('input');
        combinedInput.type = 'hidden';
        combinedInput.name = input.name;
        input.name = '';
    }

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'calendar-sheet-trigger input input-bordered';
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-expanded', 'false');

    const sheet = document.createElement('section');
    sheet.className = 'sheet calendar-sheet';
    sheet.setAttribute('aria-hidden', 'true');
    sheet.hidden = true;

    const backdrop = document.createElement('button');
    backdrop.type = 'button';
    backdrop.className = 'sheet-backdrop calendar-sheet-backdrop';
    backdrop.setAttribute('aria-label', '閉じる');

    const panel = document.createElement('div');
    panel.className = 'sheet-panel calendar-sheet-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');

    const handle = document.createElement('div');
    handle.className = 'calendar-sheet-handle handle-bar';
    handle.setAttribute('aria-hidden', 'true');

    const header = document.createElement('header');
    header.className = 'calendar-sheet-header';

    const title = document.createElement('h3');
    title.className = 'calendar-sheet-title';
    title.textContent = input.dataset.placeholder || '日付を選択';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'calendar-sheet-close';
    closeBtn.textContent = '閉じる';

    const body = document.createElement('div');
    body.className = 'calendar-sheet-body no-drag';

    // flatpickr のレンダリング先（body とは別の子要素）
    const fpContainer = document.createElement('div');
    fpContainer.className = 'calendar-sheet-fp';
    body.appendChild(fpContainer);

    header.append(title, closeBtn);
    panel.append(handle, header, body);
    sheet.append(backdrop, panel);

    input.insertAdjacentElement('afterend', trigger);
    if (combinedInput) trigger.insertAdjacentElement('afterend', combinedInput);
    document.body.appendChild(sheet);

    const state: CalendarState = {
        input,
        trigger,
        sheet,
        panel,
        backdrop,
        body,
        fpContainer,
        closeBtn,
        controller: null!,
        fp: null,
        times,
        sideSlots,
        combinedInput,
        slotsWrapper: null,
    };

    state.controller = createSharedSheetController({
        root: sheet,
        trigger,
        panel,
        backdrop,
        onBeforeOpen: () => {
            if (!state.fp) return;
            if (input.value) {
                state.fp.setDate(input.value, false);
            } else {
                state.fp.clear(false);
            }
        },
    });

    return state;
}

function bind(state: CalendarState): void {
    const hasTimes = state.times.length > 0;

    const fpResult = flatpickr(state.fpContainer, {
        inline: true,
        locale: Japanese,
        dateFormat: 'Y-m-d',
        defaultDate: state.input.value || undefined,
        minDate: state.input.min || undefined,
        maxDate: state.input.max || undefined,
        onChange(_selectedDates, dateStr) {
            state.input.value = dateStr;
            state.input.dispatchEvent(new Event('input', { bubbles: true }));
            state.input.dispatchEvent(new Event('change', { bubbles: true }));
            syncTriggerLabel(state);
            if (hasTimes) {
                syncCombinedInput(state, dateStr);
                // 日付だけではまだ閉じない（時間選択を待つ）
            } else {
                state.controller.close();
            }
        },
        onDayCreate: onDayCreateHoliday,
    });
    state.fp = Array.isArray(fpResult) ? fpResult[0] : fpResult;

    // 時間スロットの構築
    if (hasTimes) {
        const slotsTitle = state.input.dataset.flatpickrSlotsTitle ?? '';

        const { panel: slotsPanel, wrapper: slotsWrapper } = buildTimeSlotsPanel(
            state.times,
            slotsTitle,
            (_time) => {
                syncCombinedInput(state, state.input.value);
                // 日付が選択済みなら閉じる
                if (state.input.value) state.controller.close();
            },
        );
        state.slotsWrapper = slotsWrapper;

        if (state.sideSlots && state.fp) {
            // .flatpickr-calendar と .flatpickr-slots-panel を flatpickr-layout の同階層に配置
            const layout = document.createElement('div');
            layout.className = 'flatpickr-layout';
            layout.appendChild(state.fp.calendarContainer);
            layout.appendChild(slotsPanel);
            state.fpContainer.replaceWith(layout);
        } else {
            state.body.appendChild(slotsPanel);
        }
    }

    state.trigger.addEventListener('click', () => {
        if (!isMobile()) return;
        state.controller.open();
    });

    state.backdrop.addEventListener('click', () => state.controller.close());
    state.closeBtn.addEventListener('click', () => state.controller.close());

    let syncScheduled = false;
    const scheduleSync = () => {
        if (syncScheduled) return;
        syncScheduled = true;
        queueMicrotask(() => {
            syncScheduled = false;
            syncTriggerLabel(state);
        });
    };
    state.input.addEventListener('change', scheduleSync);
    state.input.addEventListener('input', scheduleSync);
}

function syncCombinedInput(state: CalendarState, dateStr: string): void {
    if (!state.combinedInput || !dateStr) return;
    const selectedBtn = state.slotsWrapper?.querySelector<HTMLButtonElement>(
        '.flatpickr-time-slot.is-selected',
    );
    const time = selectedBtn?.dataset.time ?? state.times[0];
    state.combinedInput.value = `${dateStr} ${time}`;
}

function syncTriggerLabel(state: CalendarState): void {
    const placeholder = state.input.dataset.placeholder || '日付を選択';
    const value = state.input.value;
    if (value) {
        const date = new Date(`${value}T00:00:00`);
        state.trigger.textContent = `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
        state.trigger.classList.remove('calendar-sheet-placeholder');
    } else {
        state.trigger.textContent = placeholder;
        state.trigger.classList.add('calendar-sheet-placeholder');
    }
}

function isMobile(): boolean {
    return window.matchMedia('(max-width: 767px)').matches;
}
